import { useState } from 'react';
import { useIdentity } from '@vp/auth-react';
import { API_ENDPOINTS } from '../../config/api-config';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { IIdentityObject } from '../../interfaces/app-interfaces';
import { getRestId } from '../../utils/session-storage-utils';

export function useMIAWApi() {
  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();
  const identityObj = identity as IIdentityObject;
  const { authorizationHeader } = identityObj;

  // syncCustomerUrlToChat states
  const [syncUrlLoading, setSyncUrlLoading] = useState(false);
  const [syncUrlError, setSyncUrlError] = useState<Error | null>(null);

  // updateOrderHelpCaseDescription states
  const [updateDescLoading, setUpdateDescLoading] = useState(false);
  const [updateDescError, setUpdateDescError] = useState<Error | null>(null);

  const syncCustomerUrlToChat = async (conversationId: string, currentUrl: string, currentPath: string): Promise<void> => {
    setSyncUrlLoading(true);
    setSyncUrlError(null);
    const requestURL = new URL(`${API_ENDPOINTS.CONCORD_BASE_URL()}/miaw-bridge/conversation/context`);
    requestURL.searchParams.append('request_id', getRestId());

    try {
      const response = await fetch(requestURL, {
        headers: {
          'content-type': 'application/json',
          Authorization: authorizationHeader,
        },
        method: 'POST',
        body: JSON.stringify({
          currentUrl,
          currentPath,
          conversationId,
        }),
      });
      if (response.status !== 200) {
        logCustomAttributesNR('ca-miaw-bridge-api-error', {
          logLevel: 'error',
          message: 'Unable to sync page url',
          statusCode: response.status,
          conversationId,
          apiType: 'syncCustomerUrlToChat',
        });
      }
      logCustomAttributesNR('ca-miaw-bridge-api-response', {
        logLevel: 'info',
        message: 'Page url synched in Salesforce',
        statusCode: response.status,
        conversationId,
        apiType: 'syncCustomerUrlToChat',
      });
    } catch (error) {
      setSyncUrlError(error);
      console.error(error);
      logCustomAttributesNR('ca-miaw-bridge-api-error', {
        errorName: error.name,
        errorStack: error.stack,
        message: error.message,
        logLevel: 'error',
        conversationId,
        apiType: 'syncCustomerUrlToChat',
      });
    } finally {
      setSyncUrlLoading(false);
    }
  };

  const updateOrderHelpCaseDescription = async (conversationId: string, description: string): Promise<void> => {
    setUpdateDescLoading(true);
    setUpdateDescError(null);
    const RequestURL = `${API_ENDPOINTS.CONCORD_BASE_URL()}/miaw-bridge/case/description`;
    try {
      const response = await fetch(RequestURL, {
        headers: {
          'content-type': 'application/json',
          Authorization: authorizationHeader,
        },
        method: 'POST',
        body: JSON.stringify({
          description,
          conversationId,
        }),
      });
      if (response.status !== 200) {
        logCustomAttributesNR('ca-miaw-bridge-api-error', {
          message: 'Unable to sync order help spoke description',
          logLevel: 'error',
          statusCode: response.status,
          conversationId,
          apiType: 'updateOrderHelpCaseDescription',
        });
      }
      logCustomAttributesNR('ca-miaw-bridge-api-response', {
        logLevel: 'info',
        message: 'Case description synched in Salesforce',
        statusCode: response.status,
        conversationId,
        apiType: 'updateOrderHelpCaseDescription',
      });
    } catch (error) {
      setUpdateDescError(error);
      console.error(error);
      logCustomAttributesNR('ca-miaw-bridge-api-error', {
        errorName: error.name,
        errorStack: error.stack,
        message: error.message,
        logLevel: 'error',
        conversationId,
        apiType: 'updateOrderHelpCaseDescription',
      });
    } finally {
      setUpdateDescLoading(false);
    }
  };

  return {
    // syncCustomerUrlToChat
    syncCustomerUrlToChat,
    syncUrlLoading,
    syncUrlError,
    // updateOrderHelpCaseDescription
    updateOrderHelpCaseDescription,
    updateDescLoading,
    updateDescError,
  };
}
